import React, { useState } from 'react';
import { Button, Icon, Label, Loader, Modal, Popup, SemanticCOLORS } from 'semantic-ui-react';
import styled from 'styled-components';
import { useToasts } from 'common/toast';
import {
  AIPromptEvalRun,
  EvalCaseResult,
  EvalRecommendation,
  EvalRunStatus,
  EvalSubject,
  EvalTierResult,
  useProposeFixMutation,
  useRunEvalMutation,
} from '../api/aiPromptApi';
import { useLatestEval } from '../hooks/useLatestEval';
import { DiffRow, lineDiff } from '../utils/lineDiff';

// ── plain-English labels for internal names ──────────────────────────────────

const CHECK_LABELS: Record<string, string> = {
  structure: 'Output format',
  theme_bounds: 'Theme values',
  keyword_bounds: 'Keyword values',
  sentiment_bounds: 'Sentiment scores',
  distribution_bounds: 'Sentiment buckets',
  nonempty_summaries: 'Summaries present',
  summary_bullets: 'Summary bullets',
  pillar_summary_length: 'Summary length',
  calls: 'AI calls',
  expectations: 'Case checks',
  llm_judge: 'AI judge',
};

const SOURCE_LABELS: Record<string, string> = {
  ...CHECK_LABELS,
  chat_expectations: 'Case checks',
};

const SECTION_LABELS: Record<string, string> = {
  instructions: 'Instructions',
  example: 'Example Output',
  instructions_text: 'Initial Analysis Instructions',
  followup_instructions_text: 'Follow-up Turn Instructions',
};

const label = (map: Record<string, string>, key: string) =>
  map[key] ?? key.replace(/_/g, ' ');

const SEVERITY_ORDER: Record<EvalRecommendation['severity'], number> = { high: 0, medium: 1, low: 2 };
const severityColor = (severity: EvalRecommendation['severity']): SemanticCOLORS =>
  severity === 'high' ? 'red' : severity === 'medium' ? 'orange' : 'grey';

const formatDate = (dateString?: string | null) =>
  dateString
    ? new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'never';

// ── styles ───────────────────────────────────────────────────────────────────

const Summary = styled.p`
  font-size: 15px;
  margin: 0 0 4px;
  color: #222;
`;

const StaleNote = styled.p`
  font-size: 13px;
  color: #b25e09;
  margin: 0 0 12px;
`;

const Section = styled.section`
  margin-top: 18px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 8px;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #777;
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 0;
  font-size: 14px;
`;

const RowText = styled.span`
  flex: 1;
`;

const Muted = styled.span`
  color: #888;
  font-size: 13px;
`;

const Toggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #2185d0;
  font-size: 13px;
  cursor: pointer;
`;

const Detail = styled.div`
  margin: 2px 0 6px 24px;
  font-size: 13px;
  color: #555;
`;

const RecItem = styled.li`
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.45;
`;

const Changelog = styled.pre`
  font-size: 13px;
  white-space: pre-wrap;
  margin: 0 0 10px;
  color: #333;
  font-family: inherit;
`;

const DiffTable = styled.div`
  font-family: 'Monaco', 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 6px;
`;

const DIFF_COLORS: Record<'A' | 'M' | 'D' | 'ctx' | 'gap', { bg: string; marker: string }> = {
  A: { bg: '#e6ffed', marker: '#1a7f37' },
  M: { bg: '#fff8e1', marker: '#9a6700' },
  D: { bg: '#ffebe9', marker: '#cf222e' },
  ctx: { bg: '#fff', marker: '#bbb' },
  gap: { bg: '#f6f8fa', marker: '#bbb' },
};

const DiffLine = styled.div<{ $kind: keyof typeof DIFF_COLORS }>`
  display: flex;
  background: ${(p) => DIFF_COLORS[p.$kind].bg};
  ${(p) => (p.$kind === 'gap' ? 'color: #999; font-style: italic;' : '')}
`;

const DiffMarker = styled.span<{ $kind: keyof typeof DIFF_COLORS }>`
  flex: 0 0 28px;
  text-align: center;
  font-weight: bold;
  color: ${(p) => DIFF_COLORS[p.$kind].marker};
  user-select: none;
`;

const DiffText = styled.span<{ $struck?: boolean }>`
  flex: 1;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 0 8px 0 0;
  ${(p) => (p.$struck ? 'text-decoration: line-through; color: #999;' : '')}
`;

const Footer = styled.div`
  margin-top: 20px;
  font-size: 12px;
  color: #999;
`;

// ── grade / status ───────────────────────────────────────────────────────────

export type EvalVerdict = 'none' | 'running' | 'failed' | 'stale' | 'good' | 'warn' | 'fail';

export const evalVerdict = (run: AIPromptEvalRun | null): EvalVerdict => {
  if (!run) return 'none';
  if (run.status === EvalRunStatus.Pending || run.status === EvalRunStatus.Running) return 'running';
  if (run.status === EvalRunStatus.Failed) return 'failed';
  if (run.is_stale) return 'stale';
  const grade = run.grade || (run.passed ? 'good' : 'fail');
  return grade === 'good' ? 'good' : grade === 'warn' ? 'warn' : 'fail';
};

const VERDICT_LABEL: Record<EvalVerdict, { text: string; color?: SemanticCOLORS; testId: string }> = {
  none: { text: 'Not evaluated', testId: 'eval-status-none' },
  running: { text: 'Running…', color: 'blue', testId: 'eval-status-running' },
  failed: { text: 'Eval failed', color: 'red', testId: 'eval-status-failed' },
  stale: { text: 'Not evaluated — prompt changed', color: 'orange', testId: 'eval-status-stale' },
  good: { text: 'Good', color: 'green', testId: 'eval-status-passed' },
  warn: { text: 'Needs attention', color: 'orange', testId: 'eval-status-warn' },
  fail: { text: 'Fail', color: 'red', testId: 'eval-status-not-passed' },
};

export const EvalStatusLabel = ({ run, size }: { run: AIPromptEvalRun | null; size?: 'mini' | 'small' }) => {
  const verdict = evalVerdict(run);
  const { text, color, testId } = VERDICT_LABEL[verdict];
  return (
    <Label color={color} size={size} data-testid={testId}>
      {verdict === 'running' && <Loader active inline size="mini" style={{ marginRight: 6 }} />}
      {verdict === 'good' && <Icon name="check" />}
      {verdict === 'warn' && <Icon name="warning sign" />}
      {verdict === 'fail' && <Icon name="close" />}
      {text}
    </Label>
  );
};

const summaryFor = (run: AIPromptEvalRun, verdict: EvalVerdict) => {
  const failed = run.tier_results.filter((t) => !t.skipped && !t.passed);
  const judge = run.judge_score !== null ? ` The AI judge rated it ${run.judge_score}/5.` : '';
  switch (verdict) {
    case 'running':
      return 'Running the prompt against mock data with real AI calls…';
    case 'failed':
      return `The eval could not complete: ${run.error || 'unknown error'}`;
    case 'fail':
      return failed.length
        ? `${failed.length} ${failed.length === 1 ? 'check' : 'checks'} failed: ${failed
            .map((t) => label(CHECK_LABELS, t.name).toLowerCase())
            .join(', ')}.${judge}`
        : `The output fell short.${judge}`;
    case 'warn':
      return `Every check passed, but the AI judge found things worth fixing.${judge}`;
    default:
      return `Looks good — every check passed.${judge}`;
  }
};

// ── sub-sections ─────────────────────────────────────────────────────────────

const CheckRow = ({ tier }: { tier: EvalTierResult }) => {
  const [open, setOpen] = useState(false);
  const icon = tier.skipped ? (
    <Icon name="minus circle" color="grey" />
  ) : tier.passed ? (
    <Icon name="check circle" color="green" />
  ) : (
    <Icon name="times circle" color="red" />
  );
  const score = tier.score !== null && tier.max_score !== null ? `${tier.score}/${tier.max_score}` : null;
  const showDetail = !tier.passed || tier.skipped;
  return (
    <>
      <Row data-testid={`eval-tier-${tier.name}`}>
        {icon}
        <RowText>
          {label(CHECK_LABELS, tier.name)}
          {score && <Muted> · {score}</Muted>}
        </RowText>
        {showDetail && tier.details && (
          <Toggle type="button" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Why?'}
          </Toggle>
        )}
      </Row>
      {open && <Detail>{tier.details}</Detail>}
    </>
  );
};

const CaseRow = ({ c }: { c: EvalCaseResult }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = c.problems.length > 0 || !!c.judge_rationale || !!c.error;
  return (
    <>
      <Row data-testid={`eval-case-${c.id}`}>
        {c.passed ? <Icon name="check circle" color="green" /> : <Icon name="times circle" color="red" />}
        <RowText>{c.name}</RowText>
        {c.judge_score !== null && (
          <Label size="mini" basic color={c.judge_score >= 4 ? 'green' : c.judge_score >= 3 ? 'orange' : 'red'}>
            {c.judge_score}/5
          </Label>
        )}
        {hasDetail && (
          <Toggle type="button" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'Details'}
          </Toggle>
        )}
      </Row>
      {open && (
        <Detail>
          {c.error && <div>Call failed: {c.error}</div>}
          {c.problems.map((p) => (
            <div key={p}>✗ {p}</div>
          ))}
          {c.judge_rationale && <div style={{ fontStyle: 'italic' }}>{c.judge_rationale}</div>}
        </Detail>
      )}
    </>
  );
};

/** A proposed section as A / M / D rows against the current saved text. */
const DiffView = ({ before, after, testId }: { before: string; after: string; testId: string }) => {
  const rows: DiffRow[] = lineDiff(before, after);
  return (
    <DiffTable data-testid={testId}>
      {rows.map((row, idx) => {
        if (row.kind === 'gap') {
          return (
            <DiffLine key={idx} $kind="gap" data-testid="diff-row-gap">
              <DiffMarker $kind="gap">…</DiffMarker>
              <DiffText>{row.count} unchanged {row.count === 1 ? 'line' : 'lines'}</DiffText>
            </DiffLine>
          );
        }
        if (row.kind === 'M') {
          return (
            <React.Fragment key={idx}>
              <DiffLine $kind="M" data-testid="diff-row-M">
                <DiffMarker $kind="M">M</DiffMarker>
                <DiffText>{row.text || ' '}</DiffText>
              </DiffLine>
              <DiffLine $kind="M">
                <DiffMarker $kind="M" />
                <DiffText $struck>{row.oldText || ' '}</DiffText>
              </DiffLine>
            </React.Fragment>
          );
        }
        return (
          <DiffLine key={idx} $kind={row.kind} data-testid={`diff-row-${row.kind}`}>
            <DiffMarker $kind={row.kind}>{row.kind === 'ctx' ? '' : row.kind}</DiffMarker>
            <DiffText $struck={row.kind === 'D'}>{row.text || ' '}</DiffText>
          </DiffLine>
        );
      })}
    </DiffTable>
  );
};

const RecommendationItem = ({ rec }: { rec: EvalRecommendation }) => {
  const [open, setOpen] = useState(false);
  return (
    <RecItem data-testid="eval-recommendation">
      <Label size="mini" color={severityColor(rec.severity)} style={{ marginRight: 6, verticalAlign: 'middle' }}>
        {rec.severity}
      </Label>
      {rec.recommendation}{' '}
      <Toggle type="button" onClick={() => setOpen((v) => !v)}>
        {open ? 'hide why' : 'why?'}
      </Toggle>
      {open && (
        <Detail style={{ marginLeft: 0 }}>
          <Muted>{label(SOURCE_LABELS, rec.source)}:</Muted> {rec.issue}
        </Detail>
      )}
    </RecItem>
  );
};

// ── modal ────────────────────────────────────────────────────────────────────

interface AIPromptEvalModalProps {
  open: boolean;
  onClose: () => void;
  subject: EvalSubject;
  subjectLabel: string;
  /** The subject has been saved at least once (evals run against saved text). */
  promptSaved: boolean;
  /** Current saved text per section key, for diffing a proposal against. */
  currentSections: Record<string, string>;
  /** The editor holds unsaved edits; applying a proposal would overwrite them. */
  editorDirty: boolean;
  /** Load a proposed fix's sections into the editor as unsaved changes. */
  onApplyProposal: (sections: Record<string, string>) => void;
}

export const AIPromptEvalModal = ({
  open,
  onClose,
  subject,
  subjectLabel,
  promptSaved,
  currentSections,
  editorDirty,
  onApplyProposal,
}: AIPromptEvalModalProps) => {
  const { addToast } = useToasts();
  const { data, run, isActive, isProposing, isLoading } = useLatestEval(open ? subject : null);
  const [runEval, { isLoading: isStartingEval }] = useRunEvalMutation();
  const [proposeFix, { isLoading: isRequestingFix }] = useProposeFixMutation();
  const [showMinor, setShowMinor] = useState(false);

  const supported = data?.supported ?? true;
  const verdict = evalVerdict(run);
  const showResults = !!run && run.status === EvalRunStatus.Succeeded;
  const isStale = verdict === 'stale';

  const recommendations = [...(run?.recommendations ?? [])].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );
  const major = recommendations.filter((r) => r.severity !== 'low');
  const minor = recommendations.filter((r) => r.severity === 'low');
  const caseTier = run?.tier_results.find((t) => t.cases && t.cases.length > 0);
  const proposalEntries = Object.entries(run?.proposed_sections ?? {});
  const hasProposal = proposalEntries.length > 0;
  const proposalFailed = run?.proposal_status === 'failed';
  // A proposal drafted against a version that has since been saved over is
  // shown for reference but can't be applied: the diff no longer describes
  // the edit it would make.
  const canApply = showResults && hasProposal && !isStale && !editorDirty;
  const applyBlockedReason = !hasProposal
    ? undefined
    : isStale
      ? 'This fix was drafted for an older version. Run a new eval and propose again.'
      : editorDirty
        ? 'The editor has unsaved changes. Save or discard them before applying a fix.'
        : undefined;

  const handleRunEval = async () => {
    try {
      await runEval(subject).unwrap();
      addToast('Eval started', { appearance: 'success' });
    } catch (err: any) {
      addToast(`Failed to start eval: ${err?.data?.error || 'Unknown error'}`, { appearance: 'error' });
    }
  };

  const handleProposeFix = async () => {
    if (!run) return;
    try {
      await proposeFix({ subject, evalId: run.id }).unwrap();
      addToast('Drafting a fix — this takes about a minute', { appearance: 'info' });
    } catch (err: any) {
      addToast(`Failed to propose a fix: ${err?.data?.error || 'Unknown error'}`, { appearance: 'error' });
    }
  };

  const handleApplyProposal = () => {
    if (!run) return;
    onApplyProposal(run.proposed_sections);
    addToast('Proposal loaded into the editor — review, then Save and re-run the eval', { appearance: 'info' });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} size="small" closeIcon data-testid="eval-modal">
      <Modal.Header>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span>Eval — {subjectLabel}</span>
          {supported && !isLoading && <EvalStatusLabel run={run} size="small" />}
        </span>
      </Modal.Header>

      <Modal.Content scrolling>
        {!supported ? (
          <Muted data-testid="eval-unsupported">Evals are not available for this prompt type.</Muted>
        ) : isLoading ? (
          <Loader active inline="centered" />
        ) : !run ? (
          <>
            <Summary>This prompt has not been evaluated yet.</Summary>
            <Muted>
              Running an eval sends the saved prompt through the real pipeline with mock data, checks the
              output, and asks an AI judge to rate it. Nothing changes until you choose to apply a fix.
            </Muted>
          </>
        ) : (
          <>
            <Summary data-testid="eval-summary">{summaryFor(run, verdict)}</Summary>
            {verdict === 'stale' && (
              <StaleNote>
                This result is for v{run.prompt_version}; the prompt has been saved since. Run a new eval to
                re-validate.
              </StaleNote>
            )}
            {verdict === 'failed' && run.error && <Detail data-testid="eval-error">{run.error}</Detail>}

            {showResults && (
              <div data-testid="eval-results" style={{ opacity: verdict === 'stale' ? 0.6 : 1 }}>
                <Section>
                  <SectionTitle>Checks</SectionTitle>
                  {run.tier_results.map((tier) => (
                    <CheckRow key={tier.name} tier={tier} />
                  ))}
                  {run.judge_rationale && !caseTier && (
                    <Detail data-testid="eval-judge" style={{ fontStyle: 'italic' }}>
                      {run.judge_rationale}
                    </Detail>
                  )}
                </Section>

                {caseTier?.cases && (
                  <Section>
                    <SectionTitle>Cases</SectionTitle>
                    {run.judge_rationale && (
                      <Muted data-testid="eval-judge" style={{ display: 'block', marginBottom: 6 }}>
                        {run.judge_rationale}
                      </Muted>
                    )}
                    {caseTier.cases.map((c) => (
                      <CaseRow key={c.id} c={c} />
                    ))}
                  </Section>
                )}

                {recommendations.length > 0 && (
                  <Section data-testid="eval-recommendations">
                    <SectionTitle>What to fix</SectionTitle>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {major.map((rec, i) => (
                        <RecommendationItem key={`${rec.source}-${i}`} rec={rec} />
                      ))}
                      {showMinor && minor.map((rec, i) => <RecommendationItem key={`minor-${i}`} rec={rec} />)}
                    </ul>
                    {minor.length > 0 && (
                      <Toggle type="button" onClick={() => setShowMinor((v) => !v)} data-testid="toggle-minor">
                        {showMinor ? 'Hide minor suggestions' : `Show ${minor.length} minor suggestion${minor.length === 1 ? '' : 's'}`}
                      </Toggle>
                    )}
                  </Section>
                )}

                {isProposing && (
                  <Section data-testid="eval-proposal-pending">
                    <SectionTitle>Proposed fix</SectionTitle>
                    <Muted>
                      <Loader active inline size="mini" style={{ marginRight: 6 }} />
                      Drafting a fix from the recommendations…
                    </Muted>
                  </Section>
                )}

                {proposalFailed && !isProposing && (
                  <Section data-testid="eval-proposal-failed">
                    <SectionTitle>Proposed fix</SectionTitle>
                    <Detail style={{ marginLeft: 0, color: '#cf222e' }}>
                      Could not draft a fix: {run.proposal_error || 'unknown error'}
                    </Detail>
                  </Section>
                )}

                {hasProposal && !isProposing && (
                  <Section data-testid="eval-proposal">
                    <SectionTitle>Proposed fix</SectionTitle>
                    {applyBlockedReason && (
                      <StaleNote data-testid="eval-proposal-blocked">{applyBlockedReason}</StaleNote>
                    )}
                    {run.proposal_changelog && (
                      <Changelog data-testid="eval-proposal-changelog">{run.proposal_changelog}</Changelog>
                    )}
                    {proposalEntries.map(([key, text]) => (
                      <div key={key} style={{ marginTop: 8 }}>
                        {proposalEntries.length > 1 && <Muted>{label(SECTION_LABELS, key)}</Muted>}
                        <DiffView before={currentSections[key] ?? ''} after={text} testId={`eval-diff-${key}`} />
                      </div>
                    ))}
                  </Section>
                )}
              </div>
            )}

            <Footer>
              v{run.prompt_version} · {formatDate(run.finished_at || run.created_at)}
              {run.call_count > 0 && ` · ${run.call_count} AI calls`}
              {run.usage?.total_tokens ? ` · ${run.usage.total_tokens.toLocaleString()} tokens` : ''}
              {run.duration_ms > 0 && ` · ${(run.duration_ms / 1000).toFixed(0)}s`}
              {run.model && ` · ${run.model}`}
            </Footer>
          </>
        )}
      </Modal.Content>

      {supported && (
        <Modal.Actions>
          {showResults && recommendations.length > 0 && (
            <Button
              basic
              color="blue"
              onClick={handleProposeFix}
              disabled={isRequestingFix || isProposing || isActive}
              loading={isRequestingFix || isProposing}
              data-testid="propose-fix-button"
            >
              <Icon name="magic" /> {hasProposal ? 'Re-propose fix' : 'Propose fix'}
            </Button>
          )}
          <Popup
            content={promptSaved ? undefined : 'Save the prompt before running an eval.'}
            disabled={promptSaved}
            trigger={
              <span>
                <Button
                  color="blue"
                  basic={hasProposal}
                  onClick={handleRunEval}
                  disabled={!promptSaved || isActive || isStartingEval}
                  loading={isStartingEval}
                  data-testid="run-eval-button"
                >
                  <Icon name="clipboard check" /> {run ? 'Run again' : 'Run eval'}
                </Button>
              </span>
            }
          />
          {showResults && hasProposal && !isProposing && (
            <Popup
              content={applyBlockedReason}
              disabled={canApply}
              trigger={
                <span>
                  <Button
                    color="green"
                    onClick={handleApplyProposal}
                    disabled={!canApply}
                    data-testid="apply-proposal-button"
                  >
                    <Icon name="arrow up" /> Apply to editor
                  </Button>
                </span>
              }
            />
          )}
          <Button onClick={onClose}>Close</Button>
        </Modal.Actions>
      )}
    </Modal>
  );
};

export default AIPromptEvalModal;
